import {
  getUserProfile,
  setUserProfile,
} from '@leagueplatform/user-profile-api';
import type {
  UserProfile,
  UserProfileAggregate,
} from '@leagueplatform/user-profile-api';
import { SocketAsFetch } from '@leagueplatform/socket-as-fetch';
import type {
  MemberProfile,
  ProfileSectionId,
  SectionValues,
} from './profile-types';

// Profile read/write through the SDK contract (@leagueplatform/user-profile-api):
// `getUserProfile` resolves a UserProfileAggregate — email / phone_number at
// the aggregate level, everything else nested under `user_profile`.
//
// Saves: Personal Information and Mailing Address are `user_profile` fields
// and go through the SDK's `setUserProfile(Partial<UserProfile>)`. Contact
// Information (email, phone) lives on the AGGREGATE, which setUserProfile
// cannot modify and for which no SDK setter exists — its save path is
// unconfirmed (design.md Open Questions) and rides the raw set_user_profile
// message as a placeholder. End-to-end save verification is blocked on the
// PTTH-397 demographics PATCH stub regardless.

type MemberLocation = NonNullable<UserProfile['locations']>[number];

const asDateString = (value?: string): string => (value ?? '').slice(0, 10);

const defaultLocation = (profile: UserProfile): MemberLocation | undefined => {
  const locations = profile.locations ?? [];
  return locations.find((location) => location.isDefault) ?? locations[0];
};

// `phone_numbers` exists in the runtime response but is absent from the
// SDK's UserProfileAggregate type — read it defensively.
const mobileNumber = (aggregate: UserProfileAggregate): string => {
  const { phone_numbers: phoneNumbers } = aggregate as UserProfileAggregate & {
    phone_numbers?: { number?: string; description?: string }[];
  };
  return (
    phoneNumbers?.find((phone) => /mobile|cell/i.test(phone.description ?? ''))
      ?.number ?? ''
  );
};

export const toMemberProfile = (
  aggregate: UserProfileAggregate,
): MemberProfile => {
  const profile = aggregate.user_profile;
  const mailing = defaultLocation(profile);
  return {
    personalInformation: {
      firstName: profile.first_name ?? '',
      lastName: profile.last_name ?? '',
      preferredName: profile.preferred_first_name ?? '',
      birthDate: asDateString(profile.date_of_birth),
      gender: profile.sex ?? '',
    },
    mailingAddress: {
      streetAddress: mailing?.address1 ?? '',
      suite: mailing?.address2 ?? '',
      city: mailing?.city ?? '',
      state: mailing?.province ?? '',
      zip: mailing?.postal ?? '',
    },
    contactInformation: {
      primaryPhone: aggregate.phone_number ?? '',
      mobilePhone: mobileNumber(aggregate),
      emailAddress: aggregate.email ?? '',
    },
  };
};

export interface FetchedMemberProfile {
  memberProfile: MemberProfile;
  aggregate: UserProfileAggregate;
}

export const fetchMemberProfile = async (): Promise<FetchedMemberProfile> => {
  const aggregate = await getUserProfile();
  return { memberProfile: toMemberProfile(aggregate), aggregate };
};

export const saveMemberProfileSection = async (
  sectionId: ProfileSectionId,
  values: SectionValues,
  aggregate: UserProfileAggregate | null,
): Promise<void> => {
  if (sectionId === 'personalInformation') {
    await setUserProfile({
      first_name: values.firstName,
      last_name: values.lastName,
      preferred_first_name: values.preferredName,
      date_of_birth: values.birthDate,
      sex: values.gender,
    });
    return;
  }

  if (sectionId === 'mailingAddress') {
    // Merge the edit into the member's default location so the rest of the
    // locations array (and unrelated location fields) are preserved.
    const locations = aggregate?.user_profile.locations ?? [];
    const current =
      locations.find((location) => location.isDefault) ?? locations[0];
    // A member with no location yet gets a fresh default home entry.
    const base: MemberLocation = current ?? {
      geo_location: {},
      approximate_geo_location: {},
      type: 'home',
      isDefault: true,
    };
    const updated: MemberLocation = {
      ...base,
      address1: values.streetAddress,
      address2: values.suite,
      city: values.city,
      province: values.state,
      postal: values.zip,
    };
    const others = locations.filter((location) => location !== current);
    await setUserProfile({ locations: [updated, ...others] });
    return;
  }

  // Contact Information: no SDK setter for aggregate-level email/phone —
  // placeholder path pending the PTTH-397 fast-follow.
  await SocketAsFetch.fetch({
    message_type: 'set_user_profile',
    info: {
      email: values.emailAddress,
      phone_number: values.primaryPhone,
      ...(values.mobilePhone
        ? {
            phone_numbers: [
              { number: values.mobilePhone, description: 'mobile' },
            ],
          }
        : {}),
    },
  });
};
